"use client";
import { Skeleton } from "@/app/components/ui/skeleton";
import { generateImageUrlFromKey } from "@/lib/utils";
import { Auction } from "@prisma/client";
import { Flex, Text } from "@radix-ui/themes";
import axios from "axios";
import Image from "next/image";
import Link from "next/link";
import useSWR from "swr";
import { useEffect, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  auction: Auction;
}

interface AuctionImage {
  imageUrl: string;
  lotId: number | null;
}

const AuctionImagesPreview = ({ auction }: Props) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const fetcher = async (url: string): Promise<AuctionImage[]> => {
    const response = await axios.get(url);
    return response.data.map(
      (img: { imageKey: string; lotId: number | null }) => ({
        imageUrl: generateImageUrlFromKey(img.imageKey),
        lotId: img.lotId,
      })
    );
  };

  const { data: images, error } = useSWR(
    `/api/image/auctions/${auction.id}/preview-images`,
    fetcher,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (images && images.length > 1 && !isPaused) {
      const timer = setTimeout(() => {
        setCurrentImageIndex(
          (prevIndex) => (prevIndex + 1) % images.length
        );
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [currentImageIndex, images, isPaused]);

  const handlePrev = () => {
    if (!images) return;
    setCurrentImageIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length
    );
  };

  const handleNext = () => {
    if (!images) return;
    setCurrentImageIndex(
      (prevIndex) => (prevIndex + 1) % images.length
    );
  };

  if (error) {
    return (
      <Flex
        className="invisible h-0 md:visible md:w-1/3 md:h-80 border"
        align="center"
        justify="center"
      >
        <Text className="text-xs">Error loading images.</Text>
      </Flex>
    );
  }

  if (!images) {
    return (
      <Skeleton className="invisible h-0 md:visible md:w-1/3 md:h-80 rounded-none" />
    );
  }

  if (images.length === 0) {
    return (
      <Flex
        className="invisible h-0 md:visible md:w-1/3 md:h-80 border"
        align="center"
        justify="center"
      >
        <Text className="text-xs">No images in this auction.</Text>
      </Flex>
    );
  }

  const currentImage = images[currentImageIndex];
  const lotId = currentImage.lotId;

  return (
    <Flex
      className="invisible md:visible md:w-1/3"
      align="center"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <Link href={lotId ? `/auctions/${auction.id}/lots/${lotId}` : '#'} className="w-full">
        <Flex className="w-full h-0 md:h-80 py-5 px-3 relative overflow-hidden group">
          <AnimatePresence>
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full h-full"
            >
              <Image
                src={currentImage.imageUrl}
                alt="Lot image"
                priority
                unoptimized
                style={{ objectFit: "cover" }}
                fill
                className="hover:scale-103"
              />
            </motion.div>
          </AnimatePresence>
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.preventDefault(); handlePrev(); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronLeftIcon />
              </button>
              <button
                onClick={(e) => { e.preventDefault(); handleNext(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronRightIcon />
              </button>
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-md">
                {currentImageIndex + 1} / {images.length}
              </div>
            </>
          )}
        </Flex>
      </Link>
    </Flex>
  );
};

export default AuctionImagesPreview;
